/**
 * Rides Transfer Section Component
 * 
 * Unified split-view interface for transferring rides between Atlas and database.
 * Replaces the previous RidesDownloadSection with a cleaner, more maintainable design.
 * 
 * Features:
 * - Split-view layout displaying Atlas rides (left) and database rides (right) simultaneously
 * - Bidirectional selection for upload (left → right) and download (right → left) operations
 * - Eliminates code duplication through better component architecture
 * - Reduces visual clutter with modern card-based layout
 * 
 * @module RidesTransferSection
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Divider,
  Chip,
  LinearProgress,
  Button,
  Tooltip,
} from '@mui/material';

import { type Environment, getEnvironmentDisplayName } from './EnvironmentSelector';
import {
  type UATNetwork,
  type UATNetworkSettingsResponse,
  fetchNetworkSettings,
  type UATRideEntry,
  type UATRideDetailedEntry,
} from '../../../services/fixedRouteUatClient';
import { AtlasRidesPanel } from './rides-transfer/AtlasRidesPanel';
import { DatabaseRidesPanel } from './rides-transfer/DatabaseRidesPanel';
import { ActionButtonGroup } from './routes-transfer/ActionButtonGroup';
import { ValidationSummary } from './routes-transfer/ValidationSummary';
import { OperationResults } from './routes-transfer/OperationResults';
import { RideOperationConfirmationDialog } from './rides-transfer/RideOperationConfirmationDialog';
import { UserAssignmentModal } from './rides-transfer/UserAssignmentModal';
import { useRides } from '../../../context/RidesContext';
import { useUploadRides } from '../../../hooks/useUploadRides';
import { useDownloadRides } from '../../../hooks/useDownloadRides';
import { useDeleteRides } from '../../../hooks/useDeleteRides';
import type { RideSchema } from '../../../types/simulation';
import { useContext } from 'react';
import { AppContext } from '../../../context/context';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
/**
 * Props interface for RidesTransferSection component
 */
export interface RidesTransferSectionProps {
  /** JWT token for API authentication */
  jwtToken: string;
  /** Selected environment (UAT or Production) */
  environment: Environment;
  /** Service date for ride operations (YYYY-MM-DD) - used as default end date */
  serviceDate: string;
  /** Timezone for ride operations */
  timezone: string;
  /** Callback when service date changes */
  onServiceDateChange: (date: string) => void;
  /** Callback when timezone changes */
  onTimezoneChange: (timezone: string) => void;
  /** Available networks from UAT/Production */
  networks: UATNetwork[];
  /** Whether networks are currently loading */
  isLoadingNetworks: boolean;
  /** Error message if network loading failed */
  networkError: string | null;
  /** Selected product ID */
  selectedProductId: string;
  /** Selected network ID */
  selectedNetworkId: number | null;
  /** Callback when product selection changes */
  onProductChange: (productId: string) => void;
  /** Callback to load networks */
  onLoadNetworks: () => void;
  /** Whether download acknowledgment is checked */
  downloadAcknowledged: boolean;
  /** Callback when download acknowledgment changes */
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

/**
 * RidesTransferSection Component
 * 
 * Main component for the unified rides transfer interface.
 * Displays Atlas rides and database rides side-by-side in a split-view layout.
 */
export const RidesTransferSection: React.FC<RidesTransferSectionProps> = ({
  jwtToken,
  environment,
  serviceDate,
  timezone,
  onServiceDateChange,
  onTimezoneChange,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  const { t } = useTranslation('mapTransfer');
  // Selection state for both panels
  const [atlasSelectedRideIds, setAtlasSelectedRideIds] = useState<Set<string>>(new Set());
  const [databaseSelectedRideIds, setDatabaseSelectedRideIds] = useState<Set<string>>(new Set());
  
  // Ride counts for headers
  const [atlasRideCount, setAtlasRideCount] = useState(0);
  const [atlasFilteredCount, setAtlasFilteredCount] = useState<number | undefined>(undefined);
  const [databaseRideCount, setDatabaseRideCount] = useState(0);
  const [databaseFilteredCount, setDatabaseFilteredCount] = useState<number | undefined>(undefined);

  // Network settings state (for timezone)
  const [networkSettings, setNetworkSettings] = useState<UATNetworkSettingsResponse | null>(null);
  const [isLoadingNetworkSettings, setIsLoadingNetworkSettings] = useState(false);

  // Get rides from context for confirmation dialogs
  const { rides: atlasRides } = useRides();
  
  // Get AppContext for map layer integration
  const context = useContext(AppContext);

  // Upload functionality with useUploadRides hook
  const { uploadState, startUpload, validateRides: validateUploadRides, resetUpload } = useUploadRides();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [userAssignmentModalOpen, setUserAssignmentModalOpen] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const databasePanelRefreshRef = useRef<(() => void) | null>(null);

  // Download functionality with useDownloadRides hook
  const { downloadState, startDownload, resetDownload } = useDownloadRides();
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const atlasPanelRefreshRef = useRef<(() => void) | null>(null);

  // Delete functionality with useDeleteRides hook
  const { deleteState, startDelete, resetDelete } = useDeleteRides();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Database rides for confirmation dialogs
  const [databaseRides, setDatabaseRides] = useState<(UATRideEntry | UATRideDetailedEntry)[]>([]);

  // Date range state (required for rides API)
  // Default date range: both start and end date = today
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const initialStartDate = getTodayDate();
  const initialEndDate = getTodayDate();
  
  // Date range for database rides (right panel)
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  
  // Date range for atlas rides (left panel)
  const [atlasStartDate, setAtlasStartDate] = useState<string>(initialStartDate);
  const [atlasEndDate, setAtlasEndDate] = useState<string>(initialEndDate);
  // Layer selection for atlas rides
  const [selectedLayerName, setSelectedLayerName] = useState<string | null>(null);

  // Calculate effective timezone (prioritize network settings, fallback to prop)
  const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone || 'Australia/Sydney';

  // Load network settings when product/network changes
  // Use ref to track last loaded settings to prevent unnecessary refetches
  const lastNetworkSettingsRef = useRef<{ jwtToken: string; selectedProductId: string; environment: Environment } | null>(null);
  
  useEffect(() => {
    const currentKey = `${jwtToken}-${selectedProductId}-${environment}`;
    const lastKey = lastNetworkSettingsRef.current 
      ? `${lastNetworkSettingsRef.current.jwtToken}-${lastNetworkSettingsRef.current.selectedProductId}-${lastNetworkSettingsRef.current.environment}`
      : null;

    // Only fetch if key changed and we have prerequisites
    if (jwtToken.trim() && selectedProductId && !isLoadingNetworkSettings && currentKey !== lastKey) {
      setIsLoadingNetworkSettings(true);
      lastNetworkSettingsRef.current = { jwtToken, selectedProductId, environment };
      
      fetchNetworkSettings(jwtToken, selectedProductId, environment)
        .then((settings) => {
          setNetworkSettings(settings);
          // Update timezone from network settings if available
          if (settings?.data?.networks && settings.data.networks.length > 0) {
            const networkSettingsData = settings.data.networks[0]?.networkSettings;
            if (networkSettingsData?.timezone && networkSettingsData.timezone !== timezone) {
              onTimezoneChange(networkSettingsData.timezone);
            }
          }
        })
        .catch((error) => {
          console.warn('[RidesTransferSection] Failed to load network settings:', error);
        })
        .finally(() => {
          setIsLoadingNetworkSettings(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, selectedProductId, environment]);

  // Check if upload can be enabled
  const canUpload =
    atlasSelectedRideIds.size > 0 &&
    jwtToken.trim() !== '' &&
    selectedProductId !== '' &&
    selectedNetworkId !== null &&
    downloadAcknowledged &&
    !uploadState.isUploading &&
    !uploadState.isValidating;

  // Check if download can be enabled
  const canDownload =
    databaseSelectedRideIds.size > 0 &&
    jwtToken.trim() !== '' &&
    selectedProductId !== '' &&
    selectedNetworkId !== null &&
    downloadAcknowledged &&
    !downloadState.isDownloading &&
    !downloadState.isFetching;

  // Check if delete can be enabled
  const canDelete =
    databaseSelectedRideIds.size > 0 &&
    jwtToken.trim() !== '' &&
    selectedProductId !== '' &&
    selectedNetworkId !== null &&
    downloadAcknowledged &&
    !deleteState.isDeleting;

  // Handle upload button click - check for missing user_id first
  const handleUpload = () => {
    if (!canUpload) {
      return;
    }

    // Reset upload state when starting a new upload
    if (uploadState.results.length > 0) {
      resetUpload();
    }

    // Get selected rides from context
    const selectedRides = atlasRides.filter(ride => {
      const rideId = ride.external_reference_id || ride.ride_id || '';
      return atlasSelectedRideIds.has(rideId);
    });

    // Check if any rides are missing user_id
    const ridesNeedingUser = selectedRides.filter(ride => {
      return !ride.user_id && !ride.external_user_reference_id;
    });

    // If rides need user assignment, open user assignment modal first
    if (ridesNeedingUser.length > 0) {
      setUserAssignmentModalOpen(true);
    } else {
      // All rides have users, proceed to upload confirmation
      setUploadDialogOpen(true);
    }
  };

  // Handle user assignment modal continue - proceed to upload confirmation
  const handleUserAssignmentContinue = () => {
    setUserAssignmentModalOpen(false);
    // Reset upload state when starting a new upload
    if (uploadState.results.length > 0) {
      resetUpload();
    }
    setUploadDialogOpen(true);
  };

  // Handle upload confirmation from dialog
  const handleUploadConfirm = async (dialogServiceDate?: string) => {
    // Get network UUID from network settings
    const networkUuid = networkSettings?.data?.networks?.find(
      (n) => n.networkSettings?.dbId === selectedNetworkId
    )?.networkSettings?.id;

    if (!networkUuid) {
      console.error('[RidesTransferSection] Network UUID not found');
      return;
    }

    // Get service area city from network settings (if available)
    const serviceAreaCity = networkSettings?.data?.networks?.[0]?.networkSettings?.address?.label;

    // Use selected service date from dialog, or fall back to serviceDate prop
    const uploadServiceDate = dialogServiceDate || serviceDate;

    // Start upload (use effective timezone from network settings)
    await startUpload(atlasSelectedRideIds, {
      jwtToken,
      productId: selectedProductId,
      networkId: selectedNetworkId,
      networkUuid,
      timezone: effectiveTimezone,
      downloadAcknowledged,
      environment,
      serviceAreaCity,
      serviceDate: uploadServiceDate, // Pass selected service date
    });

    // Note: Refresh will be handled by the useEffect that watches upload completion
  };

  // Handle delete button click - open confirmation dialog
  const handleDelete = () => {
    if (!canDelete) {
      return;
    }

    // Reset delete state when starting a new delete
    if (deleteState.results.length > 0) {
      resetDelete();
    }

    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation from dialog
  const handleDeleteConfirm = async () => {
    // Get network UUID from network settings
    const networkUuid = networkSettings?.data?.networks?.find(
      (n) => n.networkSettings?.dbId === selectedNetworkId
    )?.networkSettings?.id;

    if (!networkUuid) {
      console.error('[RidesTransferSection] Network UUID not found');
      return;
    }

    // Get selected rides from database panel
    const selectedRides = databaseRides.filter(ride => 
      databaseSelectedRideIds.has(ride.id)
    );

    if (selectedRides.length === 0) {
      console.error('[RidesTransferSection] No rides selected for deletion');
      return;
    }

    // Start delete
    await startDelete(databaseSelectedRideIds, selectedRides, {
      jwtToken,
      productId: selectedProductId,
      networkUuid,
      environment,
    });

    // Refresh database panel after delete completes
    setTimeout(() => {
      if (databasePanelRefreshRef.current) {
        databasePanelRefreshRef.current();
      }
    }, 1000);
  };

  // Handle download button click - open confirmation dialog
  const handleDownload = () => {
    if (!canDownload) {
      return;
    }
    setDownloadDialogOpen(true);
  };

  // Handle download confirmation from dialog
  const handleDownloadConfirm = async () => {
    // Start download
    await startDownload(databaseSelectedRideIds, {
      jwtToken,
      productId: selectedProductId,
      networkId: selectedNetworkId,
      startDate,
      endDate,
      timezone,
      downloadAcknowledged,
      environment,
      context, // Pass context for map layer integration
    });

    // Refresh Atlas panel after download completes
    setTimeout(() => {
      if (atlasPanelRefreshRef.current) {
        atlasPanelRefreshRef.current();
      }
      // Auto-select the layer that was created during download
      if (downloadState.importedLayerName) {
        setSelectedLayerName(downloadState.importedLayerName);
      }
    }, 500);
  };

  // Get selected rides for confirmation dialogs
  const getSelectedAtlasRides = (): RideSchema[] => {
    return atlasRides.filter(r => {
      const rideId = r.ride_id || r.external_reference_id || '';
      return atlasSelectedRideIds.has(rideId);
    });
  };

  const getSelectedDatabaseRides = (): (UATRideEntry | UATRideDetailedEntry)[] => {
    return databaseRides.filter(r => databaseSelectedRideIds.has(r.id));
  };

  // Auto-close dialogs when operations complete
  useEffect(() => {
    if (uploadDialogOpen && !uploadState.isUploading && uploadState.results.length > 0) {
      // Check if all uploads completed (success or error)
      const allCompleted = Array.from(uploadState.progress.values()).every(
        (p) => p.status === 'success' || p.status === 'error'
      );
      if (allCompleted) {
        setUploadDialogOpen(false);
        // Refresh database panel after upload completes
        setTimeout(() => {
          if (databasePanelRefreshRef.current) {
            console.log('[RidesTransferSection] Upload complete, refreshing database rides panel...');
            databasePanelRefreshRef.current();
          }
        }, 1000); // Give server a moment to process the upload
        // Reset upload state after auto-closing dialog to allow reopening
        setTimeout(() => {
          resetUpload();
        }, 100);
      }
    }
  }, [uploadDialogOpen, uploadState.isUploading, uploadState.results.length, uploadState.progress, resetUpload]);

  // Auto-close delete dialog when delete completes
  useEffect(() => {
    if (deleteDialogOpen && !deleteState.isDeleting && deleteState.results.length > 0) {
      // Check if all deletes completed (success or error)
      const allCompleted = Array.from(deleteState.progress.values()).every(
        (p) => p.status === 'success' || p.status === 'error'
      );
      if (allCompleted) {
        setDeleteDialogOpen(false);
        // Refresh database panel after delete completes
        setTimeout(() => {
          if (databasePanelRefreshRef.current) {
            console.log('[RidesTransferSection] Delete complete, refreshing database rides panel...');
            databasePanelRefreshRef.current();
          }
        }, 1000); // Give server a moment to process the delete
        // Clear selection after successful delete
        setDatabaseSelectedRideIds(new Set());
        // Reset delete state after auto-closing dialog to allow reopening
        setTimeout(() => {
          resetDelete();
        }, 100);
      }
    }
  }, [deleteDialogOpen, deleteState.isDeleting, deleteState.results.length, deleteState.progress, resetDelete]);

  // Auto-select layer after download completes
  useEffect(() => {
    if (!downloadState.isDownloading && !downloadState.isFetching && downloadState.importedLayerName) {
      setSelectedLayerName(downloadState.importedLayerName);
    }
  }, [downloadState.isDownloading, downloadState.isFetching, downloadState.importedLayerName]);

  useEffect(() => {
    if (downloadDialogOpen && !downloadState.isDownloading && !downloadState.isFetching && downloadState.results.length > 0) {
      // Check if all downloads completed (success or error)
      const allCompleted = Array.from(downloadState.progress.values()).every(
        (p) => p.status === 'success' || p.status === 'error'
      );
      if (allCompleted) {
        setDownloadDialogOpen(false);
        // Refresh Atlas panel after download completes
        setTimeout(() => {
          if (atlasPanelRefreshRef.current) {
            atlasPanelRefreshRef.current();
          }
          // Auto-select the layer that was created during download
          if (downloadState.importedLayerName) {
            setSelectedLayerName(downloadState.importedLayerName);
          }
        }, 500);
        // Reset download state after auto-closing dialog to allow reopening
        setTimeout(() => {
          resetDownload();
        }, 100);
      }
    }
  }, [downloadDialogOpen, downloadState.isDownloading, downloadState.isFetching, downloadState.results.length, downloadState.progress, downloadState.importedLayerName, resetDownload, atlasPanelRefreshRef, setSelectedLayerName]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      {/* Split-View Container */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Left Panel - Atlas Rides */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AtlasRidesPanel
              selectedRideIds={atlasSelectedRideIds}
              onSelectionChange={setAtlasSelectedRideIds}
              onRideCountChange={(count, filteredCount) => {
                setAtlasRideCount(count);
                setAtlasFilteredCount(filteredCount);
              }}
              onRefreshRegister={(refreshFn) => {
                atlasPanelRefreshRef.current = refreshFn;
              }}
              startDate={atlasStartDate}
              endDate={atlasEndDate}
              onStartDateChange={setAtlasStartDate}
              onEndDateChange={setAtlasEndDate}
              selectedLayerName={selectedLayerName}
              onLayerChange={setSelectedLayerName}
            />
          </Box>
          
          {/* Upload Button - Associated with Atlas Panel */}
          <Box
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              p: 2,
            }}
          >
            <Tooltip title={!canUpload ? (atlasSelectedRideIds.size === 0 ? t('rides.section.uploadDisabled.noSelection') : !downloadAcknowledged ? t('rides.section.uploadDisabled.ackRequired') : t('rides.section.uploadDisabled.cannot')) : t('rides.section.uploadTooltip')}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Icon size={20} name="upload" />}
                  onClick={handleUpload}
                  disabled={!canUpload || uploadState.isUploading || downloadState.isDownloading || downloadState.isFetching || deleteState.isDeleting}
                  fullWidth
                >
                  {t('rides.section.upload')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Right Panel - Database Rides */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DatabaseRidesPanel
              jwtToken={jwtToken}
              environment={environment}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={useCallback((date: string) => {
                setStartDate(date);
              }, [])}
              onEndDateChange={useCallback((date: string) => {
                setEndDate(date);
              }, [])}
              timezone={timezone}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              networkSettings={networkSettings}
              downloadAcknowledged={downloadAcknowledged}
              selectedRideIds={databaseSelectedRideIds}
              onSelectionChange={setDatabaseSelectedRideIds}
              onRideCountChange={(count, filteredCount) => {
                setDatabaseRideCount(count);
                setDatabaseFilteredCount(filteredCount);
              }}
              onRefreshRegister={(refreshFn) => {
                databasePanelRefreshRef.current = refreshFn;
              }}
              onRidesChange={useCallback((rides: (UATRideEntry | UATRideDetailedEntry)[]) => {
                setDatabaseRides(rides);
              }, [])}
            />
          </Box>
          
          {/* Download and Delete Buttons - Associated with Database Panel */}
          <Box
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              p: 2,
              display: 'flex',
              gap: 2,
            }}
          >
            <Tooltip title={!canDownload ? (databaseSelectedRideIds.size === 0 ? t('rides.section.downloadDisabled.noSelection') : !downloadAcknowledged ? t('rides.section.downloadDisabled.ackRequired') : t('rides.section.downloadDisabled.cannot')) : t('rides.section.downloadTooltip')}>
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Icon size={20} d={ICON_FALLBACKS.CloudDownload} />}
                  onClick={handleDownload}
                  disabled={!canDownload || uploadState.isUploading || downloadState.isDownloading || downloadState.isFetching || deleteState.isDeleting}
                  fullWidth
                >
                  {t('rides.section.download')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={!canDelete ? t('rides.section.deleteDisabled.prerequisites') : t('rides.section.deleteTooltip')}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Icon size={20} name="trash" />}
                  onClick={handleDelete}
                  disabled={!canDelete || uploadState.isUploading || downloadState.isDownloading || downloadState.isFetching || deleteState.isDeleting}
                  fullWidth
                >
                  {t('rides.section.delete')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Validation Summary */}
      {showValidation && uploadState.validation && (
        <Box sx={{ flexShrink: 0, maxHeight: '300px', overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
          <ValidationSummary
            validation={{
              isValid: uploadState.validation.isValid,
              errors: uploadState.validation.errors,
              warnings: uploadState.validation.warnings,
              routeIssues: [], // Rides don't have route issues
            }}
          />
        </Box>
      )}

      {/* Upload Results */}
      {uploadState.results.length > 0 && (
        <Box sx={{ flexShrink: 0, maxHeight: '300px', overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
          <OperationResults
            results={uploadState.results}
            operationType="upload"
          />
        </Box>
      )}

      {/* Download Results */}
      {downloadState.results.length > 0 && (
        <Box sx={{ flexShrink: 0, maxHeight: '300px', overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
          <OperationResults
            results={downloadState.results}
            operationType="download"
          />
        </Box>
      )}

      {/* Delete Results */}
      {deleteState.results.length > 0 && (
        <Box sx={{ flexShrink: 0, maxHeight: '300px', overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
          <OperationResults
            results={deleteState.results}
            operationType="delete"
          />
        </Box>
      )}

      {/* User Assignment Modal */}
      <UserAssignmentModal
        open={userAssignmentModalOpen}
        onClose={() => setUserAssignmentModalOpen(false)}
        onContinue={handleUserAssignmentContinue}
        rides={atlasRides.filter(ride => {
          const rideId = ride.external_reference_id || ride.ride_id || '';
          return atlasSelectedRideIds.has(rideId) && (!ride.user_id && !ride.external_user_reference_id);
        })}
        jwtToken={jwtToken}
        productId={selectedProductId}
        networkId={networkSettings?.data?.networks?.find(
          (n) => n.networkSettings?.dbId === selectedNetworkId
        )?.networkSettings?.id || ''}
        environment={environment}
      />

      {/* Upload Confirmation Dialog */}
      <RideOperationConfirmationDialog
        operationType="upload"
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          // Reset upload state when dialog is manually closed
          if (uploadState.results.length > 0 && !uploadState.isUploading) {
            resetUpload();
          }
        }}
        rides={getSelectedAtlasRides().map(r => ({
          id: r.ride_id || r.external_reference_id || '',
          name: `${r.from_address || t('rides.section.pickupFallback')} → ${r.to_address || t('rides.section.dropoffFallback')}`,
          pickupAddress: r.from_address,
          dropoffAddress: r.to_address,
        }))}
        environment={environment}
        isProcessing={uploadState.isUploading}
        progress={uploadState.progress.size > 0 
          ? Array.from(uploadState.progress.values()).reduce((sum, p) => {
              if (p.status === 'success') return sum + 100;
              if (p.status === 'uploading' && p.progress) return sum + p.progress;
              return sum;
            }, 0) / uploadState.progress.size
          : 0}
        currentRide={Array.from(uploadState.progress.entries()).find(([_, p]) => p.status === 'uploading')?.[1]?.rideName}
        onConfirm={handleUploadConfirm}
        defaultServiceDate={serviceDate}
      />

      {/* Delete Confirmation Dialog */}
      <RideOperationConfirmationDialog
        operationType="delete"
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          // Reset delete state when dialog is manually closed
          if (deleteState.results.length > 0 && !deleteState.isDeleting) {
            resetDelete();
          }
        }}
        rides={getSelectedDatabaseRides().map(r => ({
          id: r.id,
          name: (() => {
            // Extract ride name from segments or use ID
            if ('segments' in r && r.segments && r.segments.length > 0) {
              const pickupSegment = r.segments.find(s => s.activity === 'pickup');
              const dropoffSegment = r.segments.find(s => s.activity === 'dropoff');
              const pickup = pickupSegment?.address?.label || t('rides.section.pickupFallback');
              const dropoff = dropoffSegment?.address?.label || t('rides.section.dropoffFallback');
              return `${pickup} → ${dropoff}`;
            }
            return r.id;
          })(),
          pickupAddress: (() => {
            if ('segments' in r && r.segments) {
              const pickupSegment = r.segments.find(s => s.activity === 'pickup');
              return pickupSegment?.address?.label;
            }
            return undefined;
          })(),
          dropoffAddress: (() => {
            if ('segments' in r && r.segments) {
              const dropoffSegment = r.segments.find(s => s.activity === 'dropoff');
              return dropoffSegment?.address?.label;
            }
            return undefined;
          })(),
        }))}
        environment={environment}
        isProcessing={deleteState.isDeleting}
        progress={deleteState.progress.size > 0 
          ? Array.from(deleteState.progress.values()).reduce((sum: number, p) => {
              if (p.status === 'success') return sum + 100;
              if (p.status === 'deleting' && p.progress) return sum + p.progress;
              return sum;
            }, 0) / deleteState.progress.size
          : 0}
        currentRide={Array.from(deleteState.progress.entries()).find(([_, p]) => p.status === 'deleting')?.[1]?.rideName}
        onConfirm={handleDeleteConfirm}
      />

      {/* Download Confirmation Dialog */}
      <RideOperationConfirmationDialog
        operationType="download"
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        rides={getSelectedDatabaseRides().map(r => {
          // Extract segments if available
          const pickupSegment = r.segments?.find(s => s.activity === 'pickup');
          const dropoffSegment = r.segments?.find(s => s.activity === 'dropoff');
          
          // Get addresses from segments, with fallbacks
          const pickupAddress = pickupSegment?.address?.label || t('rides.section.pickupLocationFallback');
          const dropoffAddress = dropoffSegment?.address?.label || t('rides.section.dropoffLocationFallback');
          
          // Calculate total passengers (each seat in tripPassengers represents one passenger)
          const totalPassengers = r.tripPassengers?.length || 0;
          
          // Format departure time
          const rawTime = r.departureTime || r.time;
          let formattedTime: string | undefined;
          if (rawTime && effectiveTimezone) {
            try {
              const date = new Date(rawTime);
              if (!isNaN(date.getTime())) {
                const formatter = new Intl.DateTimeFormat('en-US', {
                  timeZone: effectiveTimezone,
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true,
                });
                formattedTime = formatter.format(date);
              }
            } catch {
              formattedTime = rawTime;
            }
          } else if (rawTime) {
            // Fallback: try to format without timezone
            try {
              const date = new Date(rawTime);
              if (!isNaN(date.getTime())) {
                formattedTime = date.toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                });
              }
            } catch {
              formattedTime = rawTime;
            }
          }
          
          // Create ride name with fallbacks
          const rideName = `${pickupAddress} → ${dropoffAddress}`;
          
          const pickupFallback = t('rides.section.pickupLocationFallback');
          const dropoffFallback = t('rides.section.dropoffLocationFallback');
          return {
            id: r.id || `ride-${r.displayId || 'unknown'}`,
            name: rideName,
            pickupAddress: pickupAddress !== pickupFallback ? pickupAddress : undefined,
            dropoffAddress: dropoffAddress !== dropoffFallback ? dropoffAddress : undefined,
            departureTime: formattedTime || rawTime || undefined,
            passengers: totalPassengers > 0 ? totalPassengers : undefined,
            status: r.status || undefined,
            tripType: r.tripType || r.type || undefined,
          };
        })}
        environment={environment}
        isProcessing={downloadState.isDownloading || downloadState.isFetching}
        progress={downloadState.progress.size > 0
          ? Array.from(downloadState.progress.values()).reduce((sum, p) => {
              if (p.status === 'success') return sum + 100;
              if (p.progress) return sum + p.progress;
              return sum;
            }, 0) / downloadState.progress.size
          : 0}
        currentRide={Array.from(downloadState.progress.entries()).find(([_, p]) => p.status === 'fetching' || p.status === 'importing')?.[1]?.rideName}
        onConfirm={handleDownloadConfirm}
      />
    </Box>
  );
};

